import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../../config';
import { Download, Trash2, AlertCircle, CheckCircle2, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

const STATUS_BADGE = {
  queued: { text: 'In Warteschlange', cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  processing: { text: 'Wird verarbeitet', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  completed: { text: 'Fertig', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  failed: { text: 'Fehler', cls: 'bg-red-500/20 text-red-300 border-red-500/30' },
};

function fmtDate(iso) {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function fmtDuration(s) {
  if (s == null) return '–';
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

export default function ScreenplayAdmin({ adminToken }) {
  const [jobs, setJobs] = useState([]);
  const [sourceMode, setSourceMode] = useState('url'); // 'url' | 'upload'
  const [url, setUrl] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [frameInterval, setFrameInterval] = useState(1.0);
  const [language, setLanguage] = useState('auto');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const [cookieStatus, setCookieStatus] = useState(null);
  const [cookieEditorOpen, setCookieEditorOpen] = useState(false);
  const [cookieContent, setCookieContent] = useState('');
  const [cookieSaving, setCookieSaving] = useState(false);

  const pollRef = useRef(null);

  const authHeaders = useCallback(
    (extra = {}) => ({
      Authorization: `Bearer ${adminToken}`,
      ...extra,
    }),
    [adminToken]
  );

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  // ─────────── Jobs ───────────
  const loadJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/screenplay/jobs`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Jobs laden fehlgeschlagen', err);
    }
  }, [authHeaders]);

  const loadCookies = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/screenplay/cookies`, { headers: authHeaders() });
      if (res.ok) setCookieStatus(await res.json());
    } catch (err) {
      console.error('Cookie-Status laden fehlgeschlagen', err);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadJobs();
    loadCookies();
  }, [loadJobs, loadCookies]);

  // Polling solange Jobs aktiv sind
  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === 'queued' || j.status === 'processing');
    if (hasActive) {
      pollRef.current = setInterval(loadJobs, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [jobs, loadJobs]);

  const submitJob = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let res;
      if (sourceMode === 'upload') {
        if (!uploadFile) {
          showMessage('error', 'Bitte eine Videodatei auswählen');
          setSubmitting(false);
          return;
        }
        // XHR für Upload-Progress
        res = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${API_URL}/api/admin/screenplay/jobs/upload`);
          xhr.setRequestHeader('Authorization', `Bearer ${adminToken}`);
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          };
          xhr.onload = () =>
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              json: () => Promise.resolve(JSON.parse(xhr.responseText || '{}')),
            });
          xhr.onerror = () => reject(new Error('Netzwerk-Fehler'));
          const fd = new FormData();
          fd.append('file', uploadFile);
          fd.append('frame_interval', String(parseFloat(frameInterval) || 1.0));
          fd.append('language', language || 'auto');
          xhr.send(fd);
        });
      } else {
        if (!url.trim()) {
          showMessage('error', 'Bitte eine Video-URL eingeben');
          setSubmitting(false);
          return;
        }
        res = await fetch(`${API_URL}/api/admin/screenplay/jobs`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            url: url.trim(),
            frame_interval: parseFloat(frameInterval) || 1.0,
            language: language || 'auto',
          }),
        });
      }

      if (res.ok) {
        const newJob = await res.json();
        setJobs((prev) => [newJob, ...prev]);
        setUrl('');
        setUploadFile(null);
        setUploadProgress(0);
        showMessage('success', 'Job angelegt – Verarbeitung läuft im Hintergrund');
      } else {
        const err = await res.json().catch(() => ({}));
        showMessage('error', err.detail || 'Job-Erstellung fehlgeschlagen');
      }
    } catch (err) {
      showMessage('error', `Netzwerk-Fehler: ${err.message}`);
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Job und Drehbuch löschen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/screenplay/jobs/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        setJobs((prev) => prev.filter((j) => j.id !== id));
      } else {
        showMessage('error', 'Löschen fehlgeschlagen');
      }
    } catch (err) {
      showMessage('error', `Netzwerk-Fehler: ${err.message}`);
    }
  };

  const downloadJob = async (job) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/screenplay/jobs/${job.id}/download`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        showMessage('error', 'Download fehlgeschlagen');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeTitle = (job.title || 'drehbuch').replace(/[^\w\s.-]/g, '').replace(/\s+/g, '-').slice(0, 80);
      a.href = url;
      a.download = `${safeTitle}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showMessage('error', `Netzwerk-Fehler: ${err.message}`);
    }
  };

  // ─────────── Cookies ───────────
  const saveCookies = async () => {
    if (!cookieContent.trim()) {
      showMessage('error', 'Cookie-Inhalt ist leer');
      return;
    }
    setCookieSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/screenplay/cookies`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ content: cookieContent }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showMessage('success', data.has_login_cookies ? 'Cookies gespeichert (Login-Cookies erkannt ✓)' : 'Cookies gespeichert – ABER keine Login-Cookies enthalten!');
        setCookieContent('');
        setCookieEditorOpen(false);
        loadCookies();
      } else {
        showMessage('error', data.detail || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      showMessage('error', `Netzwerk-Fehler: ${err.message}`);
    } finally {
      setCookieSaving(false);
    }
  };

  const deleteCookies = async () => {
    if (!window.confirm('Cookies wirklich löschen? Anschließend funktioniert YouTube-Download nicht mehr.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/screenplay/cookies`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        showMessage('success', 'Cookies gelöscht');
        loadCookies();
      }
    } catch (err) {
      showMessage('error', `Netzwerk-Fehler: ${err.message}`);
    }
  };

  // ─────────── Render ───────────
  const cookieOk = cookieStatus?.exists && cookieStatus?.has_login_cookies;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">🎬 Drehbuch-Generator</h1>
        <p className="text-white/60">Video-URL einfügen oder Datei hochladen → KI generiert ein vollständiges Drehbuch (Visuals, Dialoge, Kamera, Stimmung).</p>
      </div>

      {message && (
        <div className={`p-4 rounded-xl border ${
          message.type === 'success'
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
            : 'bg-red-500/10 border-red-500/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Cookies-Status */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {cookieOk ? (
              <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-1" />
            ) : (
              <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
            )}
            <div>
              <h3 className="text-lg font-semibold text-white">YouTube-Cookies</h3>
              <p className="text-sm text-white/60 mt-1">
                {cookieOk
                  ? `Login-Cookies aktiv (Größe: ${cookieStatus.size} B, geändert: ${fmtDate(cookieStatus.modified_at)})`
                  : cookieStatus?.exists
                  ? '⚠️ Cookies vorhanden, aber KEINE Login-Cookies erkannt — Downloads werden fehlschlagen'
                  : 'Keine Cookies hinterlegt — YouTube-Downloads werden fehlschlagen'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setCookieEditorOpen((v) => !v)}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg flex items-center gap-2 flex-shrink-0"
          >
            {cookieEditorOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Verwalten
          </button>
        </div>

        {cookieEditorOpen && (
          <div className="mt-5 space-y-3 border-t border-white/10 pt-5">
            <p className="text-sm text-white/70">
              Browser-Extension <strong>„Get cookies.txt LOCALLY"</strong> in eingelogtem YouTube-Browser ausführen, exportierten Inhalt einfügen.
              Notwendig sind Login-Cookies: <code className="text-xs bg-black/40 px-1 py-0.5 rounded">SID, HSID, SSID, APISID, SAPISID, __Secure-1PSID</code>.
            </p>
            <textarea
              value={cookieContent}
              onChange={(e) => setCookieContent(e.target.value)}
              placeholder="# Netscape HTTP Cookie File ..."
              rows={10}
              className="w-full bg-black/60 border border-white/20 rounded-lg p-3 text-white/90 text-xs font-mono"
            />
            <div className="flex gap-2">
              <button
                onClick={saveCookies}
                disabled={cookieSaving || !cookieContent.trim()}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg"
              >
                {cookieSaving ? 'Speichern…' : 'Cookies speichern'}
              </button>
              {cookieStatus?.exists && (
                <button
                  onClick={deleteCookies}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 rounded-lg"
                >
                  Cookies löschen
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Job-Formular */}
      <form onSubmit={submitJob} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-lg font-semibold text-white">Neues Drehbuch generieren</h3>

        {/* Mode-Toggle */}
        <div className="flex gap-2 p-1 bg-black/40 rounded-lg w-fit">
          <button
            type="button"
            onClick={() => setSourceMode('url')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              sourceMode === 'url' ? 'bg-emerald-500 text-black' : 'text-white/70 hover:text-white'
            }`}
          >
            🔗 Video-URL
          </button>
          <button
            type="button"
            onClick={() => setSourceMode('upload')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              sourceMode === 'upload' ? 'bg-emerald-500 text-black' : 'text-white/70 hover:text-white'
            }`}
          >
            📁 Datei hochladen
          </button>
        </div>

        {sourceMode === 'url' ? (
          <div>
            <label className="block text-sm text-white/70 mb-1">Video-URL (YouTube, Vimeo, TikTok, X, SoundCloud, Direktlink …)</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://… (YouTube, Vimeo, TikTok, .mp4-Direktlink …)"
              className="w-full bg-black/60 border border-white/20 rounded-lg p-3 text-white"
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm text-white/70 mb-1">Video-Datei (mp4, mkv, mov, webm, m4v – max. 500 MB)</label>
            <input
              type="file"
              accept=".mp4,.mkv,.mov,.webm,.m4v,video/*"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full bg-black/60 border border-white/20 rounded-lg p-3 text-white file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-white/10 file:text-white"
            />
            {uploadFile && (
              <p className="mt-2 text-xs text-white/60">
                {uploadFile.name} ({(uploadFile.size / (1024 * 1024)).toFixed(1)} MB)
              </p>
            )}
            {submitting && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-2">
                <div className="w-full bg-black/60 rounded h-2 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="mt-1 text-xs text-white/60">Upload: {uploadProgress}%</p>
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/70 mb-1">Frame-Intervall (Sek.)</label>
            <input
              type="number"
              min="0.2"
              max="10"
              step="0.1"
              value={frameInterval}
              onChange={(e) => setFrameInterval(e.target.value)}
              className="w-full bg-black/60 border border-white/20 rounded-lg p-3 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-1">Sprache</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full bg-black/60 border border-white/20 rounded-lg p-3 text-white"
            >
              <option value="auto">Auto-Erkennung</option>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting || (sourceMode === 'url' ? !url.trim() : !uploadFile)}
          className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg flex items-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : '🎬'}
          Drehbuch generieren
        </button>
      </form>

      {/* Job-Liste */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Jobs ({jobs.length})</h3>
          <button onClick={loadJobs} className="text-white/60 hover:text-white p-2" title="Aktualisieren">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {jobs.length === 0 ? (
          <p className="text-white/50 text-center py-8">Noch keine Jobs.</p>
        ) : (
          <div className="space-y-2">
            {jobs.map((job) => {
              const badge = STATUS_BADGE[job.status] || STATUS_BADGE.queued;
              return (
                <div key={job.id} className="bg-black/40 border border-white/10 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${badge.cls}`}>{badge.text}</span>
                        <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/70 border border-white/10">
                          {job.source_kind === 'upload' ? '📁 Upload' : '🔗 URL'}
                        </span>
                        <span className="text-white font-medium truncate">{job.title || job.original_filename || job.url}</span>
                      </div>
                      <div className="text-xs text-white/50 mt-1 break-all">
                        {job.source_kind === 'upload' ? job.original_filename || '(unbekannt)' : job.url}
                      </div>
                      <div className="flex gap-3 text-xs text-white/40 mt-2 flex-wrap">
                        <span>Erstellt: {fmtDate(job.created_at)}</span>
                        {job.duration_seconds != null && <span>Dauer: {fmtDuration(job.duration_seconds)}</span>}
                        {job.scene_count != null && <span>{job.scene_count} Szenen</span>}
                      </div>
                      {job.status === 'failed' && job.error && (
                        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-300">
                          {job.error}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {job.status === 'completed' && (
                        <button
                          onClick={() => downloadJob(job)}
                          className="p-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg"
                          title="Drehbuch herunterladen"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteJob(job.id)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
